import { useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { forgotPassword, getMe, login } from '../services/api'
import AuthSplit from '../components/AuthSplit'
import Modal from '../components/ui/Modal'

const fieldClass =
  'w-full h-10 px-3 rounded-lg border border-line bg-canvas text-ink text-sm outline-none focus:ring-2 focus:ring-accent'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [forgotOpen, setForgotOpen] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotMessage, setForgotMessage] = useState('')

  const redirectTo = (location.state as { redirectTo?: string } | null)?.redirectTo || '/dashboard'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await login(email, password)
      localStorage.setItem('token', data.access_token)
      if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token)
      const user = await getMe()
      localStorage.setItem('user', JSON.stringify(user))
      navigate(redirectTo)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Erro ao fazer login')
    } finally {
      setLoading(false)
    }
  }

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotLoading(true)
    setForgotMessage('')
    try {
      const data = await forgotPassword(forgotEmail)
      setForgotMessage(data.detail)
    } catch {
      setForgotMessage('Não foi possível preparar a redefinição agora.')
    } finally {
      setForgotLoading(false)
    }
  }

  return (
    <AuthSplit>
      <h1 className="text-2xl font-semibold m-0">Entrar</h1>
      <p className="text-sm text-muted mt-1.5 mb-6">Acesse a sala com o seu time de agentes.</p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-bad/10 text-bad text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium mb-1">E-mail</label>
          <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={fieldClass} placeholder="seu@email.com" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="login-password" className="text-sm font-medium">Senha</label>
            <button
              type="button"
              onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotMessage('') }}
              className="text-xs text-muted cursor-pointer"
            >
              Esqueci
            </button>
          </div>
          <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={fieldClass} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={loading} className="h-10 rounded-lg bg-ink text-surface font-semibold cursor-pointer disabled:opacity-50">
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      <p className="text-sm text-muted mt-6 m-0">
        Não tem uma conta?{' '}
        <Link to="/register" state={{ redirectTo }} className="text-accent font-medium">Cadastre-se</Link>
      </p>

      <Modal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title="Redefinir senha"
        size="sm"
        footer={
          <button type="submit" form="forgot-form" disabled={forgotLoading} className="h-9 px-3 rounded-lg bg-ink text-surface text-sm font-semibold cursor-pointer disabled:opacity-50">
            {forgotLoading ? 'Preparando...' : 'Preparar redefinição'}
          </button>
        }
      >
        <form id="forgot-form" onSubmit={handleForgot} className="flex flex-col gap-3">
          <p className="text-sm text-muted m-0">
            Informe o e-mail da conta. O envio por e-mail ainda não está ativo: a redefinição fica pronta no servidor por 30 minutos.
          </p>
          <label htmlFor="forgot-email" className="text-sm font-medium">E-mail</label>
          <input id="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={fieldClass} />
          {forgotMessage && <p className="text-sm m-0">{forgotMessage}</p>}
        </form>
      </Modal>
    </AuthSplit>
  )
}
